import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import * as ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { Toaster } from 'sonner';

import { queryClient } from '@queries';
import { Button, Inline, typography, Stack, u } from '@ui';

import BoothApp from './app/app';
import { errorStyle } from './main.css';
import './service-worker';

if (!import.meta.env.DEV) {
    Sentry.init({
        dsn: import.meta.env['VITE_PUBLIC_SENTRY_DSN'],
        integrations: [
            Sentry.replayIntegration(),
            Sentry.browserTracingIntegration(),
            Sentry.browserProfilingIntegration(),
            Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
        ],
        sendDefaultPii: true,
        replaysOnErrorSampleRate: 1.0,
        tracesSampleRate: 0.1,
        profilesSampleRate: 1.0,
        tracePropagationTargets: [/^https:\/\/kiosk.elva.golf/],
        enableLogs: true,
    });
}

const ErrorFallback = ({
    error,
    resetError,
}: {
    error: unknown;
    componentStack: string;
    eventId: string;
    resetError(): void;
}) => {
    console.error(error);
    return (
        <div role="alert" className={errorStyle}>
            <Stack gap={u(2)} alignItems="center">
                <h2 className={typography({ variant: 'h2' })}>Something went wrong</h2>
                <p className={typography({ variant: 'p' })}>We're sorry for the inconvenience. Please try again.</p>
                <Inline gap={u(2)}>
                    <Button variant="primary" onClick={resetError}>
                        Reload
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => {
                            resetError();
                            window.history.back();
                        }}
                    >
                        Go Back
                    </Button>
                </Inline>
            </Stack>
        </div>
    );
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
    <Sentry.ErrorBoundary fallback={ErrorFallback} onReset={() => window.location.reload()}>
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <BoothApp />
            </BrowserRouter>
            <Toaster position="bottom-right" richColors closeButton />
        </QueryClientProvider>
    </Sentry.ErrorBoundary>,
);
